import { Check } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { useTenant } from '@/hooks/use-tenant';
import { useNextSteps } from '@/hooks/use-next-steps';
import type { DoneChip, NextStep } from '@/lib/home-next-steps';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';
import IntegrationDialog from '@/components/integration-dialog';
import { HomeCardLink } from '@/components/home/home-card-link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const twoDigits = (n: number) => String(n).padStart(2, '0');

/**
 * "Próximos passos" (HOME-07): up to 3 pending items with their action, the
 * total of pending items ("02"), and the ✓ chips of what is already done;
 * "Tudo em dia por aqui." when nothing is pending.
 */
export function NextStepsCard() {
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const { pending, total, done, isLoading } = useNextSteps();
  const store =
    tenant.ecommercePlatform === 'shopify' ||
    tenant.ecommercePlatform === 'nuvemshop'
      ? tenant.ecommercePlatform
      : null;

  const title: Record<NextStep['id'], string> = {
    integrate: LL.home.nextSteps.items.integrate(),
    billing: LL.home.nextSteps.items.billing(),
    welcome: LL.home.nextSteps.items.welcome(),
    couponSetup: LL.home.nextSteps.items.couponSetup(),
    couponOff: LL.home.nextSteps.items.couponOff(),
  };
  const actionLabel: Record<NextStep['id'], string> = {
    integrate: LL.home.integration.connect(),
    billing: LL.home.nextSteps.actions.billing(),
    welcome: LL.home.nextSteps.actions.setUp(),
    couponSetup: LL.home.nextSteps.actions.setUp(),
    couponOff: LL.home.nextSteps.actions.turnOn(),
  };
  const doneLabel: Record<DoneChip, string> = {
    integrated: LL.home.nextSteps.done.integrated(),
    published: LL.home.nextSteps.done.published(),
    billingOk: LL.home.nextSteps.done.billingOk(),
  };

  const action = (step: NextStep) => {
    if (step.href) {
      return (
        <HomeCardLink to={step.href} className="min-h-9">
          {actionLabel[step.id]}
        </HomeCardLink>
      );
    }
    if (step.action === 'integrationDialog' && store) {
      return (
        <IntegrationDialog
          store={store}
          activateIntegrationUrl={tenant.activateIntegrationUrl}
          trigger={
            <Button
              type="button"
              size="sm"
              className={cn('w-fit', mobileLayer.touch)}
            >
              {actionLabel[step.id]}
            </Button>
          }
        />
      );
    }
    return (
      <span className="text-sm leading-relaxed text-muted-foreground">
        {LL.korzaDoesNotIntegrateWithYourStoresPlatform()}
      </span>
    );
  };

  const body = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-3 py-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      );
    }
    if (pending.length === 0) {
      return (
        <p className="py-3 text-sm text-muted-foreground">
          {LL.home.nextSteps.allDone()}
        </p>
      );
    }
    return (
      <ol aria-label={LL.home.nextSteps.title()} className="flex flex-col">
        {pending.map((step, i) => (
          <li
            key={step.id}
            className="flex gap-3.5 border-b py-3.5 last:border-b-0"
          >
            <span className="pt-0.5 text-xs font-bold tracking-[0.08em] text-[#155DFC]">
              {twoDigits(i + 1)}
            </span>
            <span className="flex min-w-0 flex-col items-start gap-1">
              <span className="text-[15px] font-semibold tracking-[-0.01em]">
                {title[step.id]}
              </span>
              {action(step)}
            </span>
          </li>
        ))}
      </ol>
    );
  };

  return (
    <Card data-home-next-steps>
      <CardContent className="flex flex-col gap-1 p-5 max-md:p-4">
        <p className="mb-1.5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          <span>{LL.home.nextSteps.title()}</span>
          <span aria-hidden="true" className="h-px flex-1 bg-border" />
          {isLoading ? null : (
            <span data-next-steps-count className="text-[#155DFC]">
              {twoDigits(total)}
            </span>
          )}
        </p>
        {body()}
        {!isLoading && done.length > 0 ? (
          <ul className="flex flex-wrap gap-2 pt-3">
            {done.map((chip) => (
              <li
                key={chip}
                data-next-steps-done
                className="inline-flex h-6 items-center gap-1.5 rounded-sm bg-muted px-2 text-xs font-semibold text-muted-foreground"
              >
                <Check aria-hidden className="size-3.5" />
                {doneLabel[chip]}
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
